import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { Check } from "lucide-react";
import { preventivoApi, type Amministratore, type Vista } from "@/api/preventivo";
import { ApiError } from "@/api/client";
import { AvvisoErrore } from "./BolletteFuori";
import { COLLEGAMENTO_NON_RIUSCITO } from "./scritte";
import { FOCUS } from "./parti";
import {
  AMMINISTRATORE, CELLULARE, CELLULARE_NON_VALIDO, EMAIL, EMAIL_NON_VALIDA, FACOLTATIVO, NOME, SALVA, SALVATAGGIO, SALVATO,
} from "./scritteLista";

type Campo = keyof Amministratore;
const CAMPI: Campo[] = ["nome", "email", "cellulare"];

interface Props {
  pid: string;
  amministratore: Amministratore;
  /** Con il link per l'amministratore spento c'è solo il nome: email e cellulare non servirebbero e sono dati personali (spec § 6). */
  linkAttivo: boolean;
  /** La vista che il server restituisce dopo ogni salvataggio. */
  onVista: (v: Vista) => void;
  /** Vero finché un campo ha un valore che il server non ha o un salvataggio è in corso: la pagina tiene fermi i tasti
   *  d'invio (la mail partirebbe all'indirizzo vecchio) e avvisa prima di uscire. */
  onPendente?: (pendente: boolean) => void;
}

const INPUT = `h-11 w-full rounded-lg border bg-card px-3 text-base text-text ${FOCUS}`;
const TASTO = `inline-flex h-11 items-center justify-center rounded-lg bg-brand-dark px-5 text-base font-semibold text-white cursor-pointer touch-manipulation transition-colors enabled:hover:bg-dark disabled:cursor-not-allowed disabled:opacity-50 focus-visible:ring-offset-2 ${FOCUS}`;

/** Chi riceve il preventivo (spec § 6). Si salva solo col tasto «Salva» (o Invio in un campo), richiesta di Alberto del
 *  3/11: tutti i campi cambiati in una chiamata, che il server controlla prima di scrivere (un'email sbagliata non salva
 *  niente). Dopo il salvataggio «Salvato» con la spunta, finché non si riscrive. Un rifiuto del server sta sotto il suo
 *  campo, un guasto sotto il tasto. La pagina lo monta con `key={pid}`: cambiando preventivo i campi ripartono. */
export function RiquadroAmministratore({ pid, amministratore, linkAttivo, onVista, onPendente }: Props) {
  const id = useId();
  const iniziali: Record<Campo, string> = {
    nome: amministratore.nome ?? "", email: amministratore.email ?? "", cellulare: amministratore.cellulare ?? "",
  };
  const [valori, setValori] = useState<Record<Campo, string>>(iniziali);
  // l'ultimo valore che il server ha: si manda solo ciò che è diverso
  const [salvato, setSalvato] = useState<Record<Campo, string>>(iniziali);
  const [errori, setErrori] = useState<Partial<Record<Campo, string>>>({});
  const [guasto, setGuasto] = useState(false);
  const [inCorso, setInCorso] = useState(false);
  const [riuscito, setRiuscito] = useState(false);
  const valoriRef = useRef(valori);
  const inVolo = useRef(false);

  const cambiati = CAMPI.filter((c) => valori[c].trim() !== salvato[c]);

  const scrivi = (campo: Campo, v: string) => {
    valoriRef.current = { ...valoriRef.current, [campo]: v };
    setValori(valoriRef.current);
    setErrori((x) => (x[campo] ? { ...x, [campo]: undefined } : x));
    setRiuscito(false);
  };

  async function salva() {
    if (inVolo.current || cambiati.length === 0) return;
    const mandati = Object.fromEntries(cambiati.map((c) => [c, valori[c].trim()])) as Partial<Record<Campo, string>>;
    inVolo.current = true;
    setInCorso(true);
    setGuasto(false);
    try {
      const v = await preventivoApi.amministratore(pid, mandati);
      // il server può riscrivere il valore (cellulare col +39): si mostra quello, se nel frattempo il campo non è cambiato
      const dalServer = { ...salvato };
      const nuovi = { ...valoriRef.current };
      for (const c of cambiati) {
        dalServer[c] = v.amministratore?.[c] ?? mandati[c] ?? "";
        if (nuovi[c].trim() === mandati[c]) nuovi[c] = dalServer[c];
      }
      valoriRef.current = nuovi;
      setValori(nuovi);
      setSalvato(dalServer);
      setErrori({});
      setRiuscito(CAMPI.every((c) => nuovi[c].trim() === dalServer[c]));
      onVista(v);
    } catch (e) {
      const dove: Campo | null = e instanceof ApiError && e.status === 422
        ? (e.detail === "email_non_valida" ? "email" : e.detail === "cellulare_non_valido" ? "cellulare" : null)
        : null;
      if (dove) setErrori({ [dove]: dove === "email" ? EMAIL_NON_VALIDA : CELLULARE_NON_VALIDO });
      else setGuasto(true);
    } finally {
      inVolo.current = false;
      setInCorso(false);
    }
  }

  const pendente = inCorso || cambiati.length > 0;
  useEffect(() => { onPendente?.(pendente); }, [pendente, onPendente]);
  useEffect(() => () => onPendente?.(false), [onPendente]);

  const invia = (e: FormEvent) => { e.preventDefault(); void salva(); };

  const campo = (c: Campo, etichetta: string, extra: { type: string; autoComplete: string; inputMode?: "email" | "tel" }, facoltativo = false) => {
    const errore = errori[c];
    const idErrore = `${id}-${c}-errore`;
    return (
      <div>
        <label htmlFor={`${id}-${c}`} className="mb-1 block text-sm font-medium text-text">
          {etichetta}{facoltativo && <> <span className="font-normal text-muted-foreground">{FACOLTATIVO}</span></>}
        </label>
        <input
          id={`${id}-${c}`}
          value={valori[c]}
          onChange={(e) => scrivi(c, e.target.value)}
          aria-invalid={errore ? true : undefined}
          aria-describedby={errore ? idErrore : undefined}
          className={`${INPUT} ${errore ? "border-red-700" : "border-border"}`}
          {...extra}
        />
        {errore && <p id={idErrore} role="alert" className="mt-1 text-sm font-medium text-red-700">{errore}</p>}
      </div>
    );
  };

  return (
    // noValidate: l'email la controlla il server, con la frase approvata sotto il campo (non il fumetto del browser)
    <form onSubmit={invia} noValidate className="sm:max-w-3xl">
      <fieldset className="rounded-lg border border-border bg-card p-4">
        <legend className="px-1 font-heading text-base font-semibold text-text">{AMMINISTRATORE}</legend>
        {/* il tasto sulla stessa riga dei campi, alla loro altezza (03/11); sul telefono sotto, largo quanto lo schermo */}
        <div className={`space-y-3 md:grid md:items-start md:gap-3 md:space-y-0 ${linkAttivo ? "md:grid-cols-[repeat(3,minmax(0,1fr))_auto]" : "max-w-lg md:grid-cols-[minmax(0,1fr)_auto]"}`}>
          {campo("nome", NOME, { type: "text", autoComplete: "organization" })}
          {linkAttivo && campo("email", EMAIL, { type: "email", autoComplete: "email", inputMode: "email" })}
          {linkAttivo && campo("cellulare", CELLULARE, { type: "tel", autoComplete: "tel", inputMode: "tel" }, true)}
          {/* pt-6 = l'altezza dell'etichetta sopra i campi: il tasto sta in riga con le caselle, non con le etichette */}
          <div className="flex min-h-11 items-center md:pt-6">
            {riuscito && !pendente ? (
              <p role="status" className="inline-flex h-11 items-center gap-1.5 whitespace-nowrap text-sm font-medium text-text">
                <Check size={18} aria-hidden="true" className="text-brand-dark" />
                {SALVATO}
              </p>
            ) : (
              <button type="submit" disabled={inCorso || cambiati.length === 0} aria-busy={inCorso} className={`${TASTO} w-full md:w-auto`}>
                {inCorso ? SALVATAGGIO : SALVA}
              </button>
            )}
          </div>
        </div>
        {guasto && (
          <div className="mt-2">
            <AvvisoErrore testo={COLLEGAMENTO_NON_RIUSCITO} onRiprova={() => { void salva(); }} />
          </div>
        )}
      </fieldset>
    </form>
  );
}
